import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../config";
import AdminLetters from "../components/AdminLetters";
import AdminPosts from "../components/AdminPosts";
import AdminDonors from "../components/AdminDonors";

const PAID_STATUS_LABELS = {
  pledged: "Pledged",
  zelle_paid: "Zelle (USD)",
  gpay_paid: "GPay (INR)",
  paid: "Paid",
  planted: "Planted",
};
const PAID_STATUSES = ["zelle_paid", "gpay_paid", "paid", "planted"];

// Every read and write goes through password-gated database functions
// (db/sritaru-admin-rpcs.sql); the public key cannot touch these rows.
function Admin() {
  // Auth state
  const [password, setPassword] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [authError, setAuthError] = useState("");

  // Data state
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [section, setSection] = useState("contributions"); // contributions | donors | letters | posts

  // Filters
  const [filterTeam, setFilterTeam] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  // Sort
  const [sortCol, setSortCol] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  // Inline editing
  const [editingId, setEditingId] = useState(null);
  const [editField, setEditField] = useState("");
  const [editValue, setEditValue] = useState("");

  // Archive
  const [deleteId, setDeleteId] = useState(null);

  const loadRows = async (pw, archived) => {
    const { data, error } = await supabase.rpc("sritaru_admin_list", {
      p_password: pw,
      p_archived: archived,
    });
    if (error) throw error;
    return data || [];
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError("");
    try {
      setAllRows(await loadRows(password, false));
      setAuthenticated(true);
    } catch (err) {
      setAuthError(err.code === "42501" ? "Incorrect password" : "Error: " + err.message);
    }
  };

  const fetchContributions = useCallback(async () => {
    setLoading(true);
    try {
      setAllRows(await loadRows(password, showArchived));
    } catch (err) {
      alert("Error loading contributions: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [password, showArchived]);

  useEffect(() => {
    if (authenticated && section === "contributions") fetchContributions();
  }, [authenticated, section, fetchContributions]);

  const contributions = allRows
    .filter((c) => !filterTeam || c.team_name === filterTeam)
    .filter((c) => !filterStatus || c.payment_status === filterStatus)
    .filter((c) => !filterDateFrom || (c.created_at || "") >= filterDateFrom)
    .filter((c) => !filterDateTo || (c.created_at || "").slice(0, 10) <= filterDateTo)
    .sort((a, b) => {
      const x = a[sortCol] ?? "";
      const y = b[sortCol] ?? "";
      const cmp = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y));
      return sortDir === "asc" ? cmp : -cmp;
    });

  const teams = [...new Set(allRows.map((c) => c.team_name).filter(Boolean))].sort();

  // Summary calculations
  const totalTrees = contributions.reduce((s, c) => s + (c.num_trees || 0), 0);
  const paidTrees = contributions
    .filter((c) => PAID_STATUSES.includes(c.payment_status))
    .reduce((s, c) => s + (c.num_trees || 0), 0);
  const pledgedTrees = contributions
    .filter((c) => c.payment_status === "pledged")
    .reduce((s, c) => s + (c.num_trees || 0), 0);

  // Team breakdown
  const teamBreakdown = {};
  contributions.forEach((c) => {
    const t = c.team_name || "No Team";
    if (!teamBreakdown[t]) teamBreakdown[t] = { trees: 0, count: 0 };
    teamBreakdown[t].trees += c.num_trees || 0;
    teamBreakdown[t].count += 1;
  });

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const startEdit = (id, field, currentValue) => {
    if (showArchived) return; // restore an entry before editing it
    setEditingId(id);
    setEditField(field);
    setEditValue(currentValue || "");
  };

  const saveEdit = async () => {
    if (!editingId || !editField) return;
    try {
      const { error } = await supabase.rpc("sritaru_admin_update", {
        p_password: password,
        p_contribution_id: String(editingId),
        p_changes: { [editField]: editValue },
      });
      if (error) throw error;
      await fetchContributions();
    } catch (err) {
      alert("Error saving: " + err.message);
    } finally {
      setEditingId(null);
      setEditField("");
      setEditValue("");
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditField("");
    setEditValue("");
  };

  const handleArchive = async (id) => {
    const reason = window.prompt("Why is this entry being removed? (kept with the archived copy)", "");
    if (reason === null) return;
    try {
      const { error } = await supabase.rpc("sritaru_admin_archive", {
        p_password: password,
        p_contribution_id: String(id),
        p_reason: reason.trim() || null,
      });
      if (error) throw error;
      setDeleteId(null);
      await fetchContributions();
    } catch (err) {
      alert("Error archiving: " + err.message);
    }
  };

  const handleRestore = async (id) => {
    try {
      const { error } = await supabase.rpc("sritaru_admin_restore", {
        p_password: password,
        p_contribution_id: String(id),
      });
      if (error) throw error;
      await fetchContributions();
    } catch (err) {
      alert("Error restoring: " + err.message);
    }
  };

  const exportCSV = () => {
    if (contributions.length === 0) return;
    const headers = Object.keys(contributions[0]);
    const csvRows = [headers.join(",")];
    contributions.forEach((row) => {
      const values = headers.map((h) => {
        const val = row[h] == null ? "" : String(row[h]);
        return `"${val.replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(","));
    });
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sritaru_contributions_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <span className="text-gray-300 ml-1">&#8693;</span>;
    return sortDir === "asc" ? (
      <span className="ml-1">&#9650;</span>
    ) : (
      <span className="ml-1">&#9660;</span>
    );
  };

  // Password gate
  if (!authenticated) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <form
          onSubmit={handleLogin}
          className="bg-white rounded-xl shadow-lg p-8 w-full max-w-sm"
        >
          <h2 className="text-2xl font-bold text-sritaru-800 mb-6 text-center">
            Admin Login
          </h2>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter admin password"
            className="w-full border border-gray-300 rounded-lg px-4 py-3 mb-4 focus:ring-2 focus:ring-sritaru-400 outline-none"
          />
          {authError && (
            <p className="text-red-500 text-sm mb-3">{authError}</p>
          )}
          <button
            type="submit"
            className="w-full bg-sritaru-600 text-white font-semibold py-3 rounded-lg hover:bg-sritaru-700 transition-colors"
          >
            Login
          </button>
        </form>
      </div>
    );
  }

  const sectionTabs = (
    <div className="flex gap-2 mb-6">
      {[
        ["contributions", "Contributions"],
        ["donors", "Donors"],
        ["letters", "Letters to a Tree"],
        ["posts", "Posts"],
      ].map(([key, label]) => (
        <button
          key={key}
          onClick={() => setSection(key)}
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${
            section === key ? "bg-sritaru-700 text-white" : "bg-white text-sritaru-700 border border-sritaru-200 hover:bg-sritaru-50"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  if (section === "donors") {
    return (
      <div className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-sritaru-800 mb-6">Admin Dashboard — donors</h1>
          {sectionTabs}
          <AdminDonors password={password} />
        </div>
      </div>
    );
  }

  if (section === "posts") {
    return (
      <div className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-sritaru-800 mb-6">Admin Dashboard — social media posts</h1>
          {sectionTabs}
          <AdminPosts password={password} />
        </div>
      </div>
    );
  }

  if (section === "letters") {
    return (
      <div className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-sritaru-800 mb-6">Admin Dashboard — letters</h1>
          {sectionTabs}
          <AdminLetters password={password} />
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-sritaru-800 mb-6">
          Admin Dashboard{showArchived ? " — removed entries" : ""}
        </h1>
        {sectionTabs}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Trees", value: totalTrees, color: "sritaru-600" },
            { label: "Paid Trees", value: paidTrees, color: "sritaru-700" },
            { label: "Pledged Trees", value: pledgedTrees, color: "earth-500" },
            {
              label: "Contributors",
              value: contributions.length,
              color: "sritaru-500",
            },
          ].map((card) => (
            <div
              key={card.label}
              className="bg-white rounded-xl shadow-md p-5 text-center border-t-4"
              style={{ borderTopColor: `var(--tw-${card.color}, #16a34a)` }}
            >
              <p className="text-sm text-gray-500">{card.label}</p>
              <p className="text-3xl font-bold text-gray-800">
                {card.value.toLocaleString()}
              </p>
            </div>
          ))}
        </div>

        {/* Team Breakdown */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-sritaru-700 mb-4">
            Team Breakdown
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 text-gray-600">Team</th>
                  <th className="text-right py-2 px-3 text-gray-600">Trees</th>
                  <th className="text-right py-2 px-3 text-gray-600">
                    Contributors
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(teamBreakdown)
                  .sort(([, a], [, b]) => b.trees - a.trees)
                  .map(([team, data]) => (
                    <tr
                      key={team}
                      className="border-b border-gray-100 hover:bg-sritaru-50"
                    >
                      <td className="py-2 px-3 font-medium">{team}</td>
                      <td className="py-2 px-3 text-right">
                        {data.trees.toLocaleString()}
                      </td>
                      <td className="py-2 px-3 text-right">{data.count}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h3 className="font-semibold text-gray-700 mb-3">Filters</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <select
              value={filterTeam}
              onChange={(e) => setFilterTeam(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sritaru-400"
            >
              <option value="">All Teams</option>
              {teams.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sritaru-400"
            >
              <option value="">All Statuses</option>
              {Object.entries(PAID_STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              placeholder="From date"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sritaru-400"
            />
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              placeholder="To date"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sritaru-400"
            />
          </div>
        </div>

        {/* Export */}
        <div className="flex justify-end gap-3 mb-4">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="border border-gray-300 text-gray-700 px-5 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            {showArchived ? "Show current entries" : "Show removed entries"}
          </button>
          <button
            onClick={exportCSV}
            className="bg-earth-500 text-white px-5 py-2 rounded-lg hover:bg-earth-600 transition-colors text-sm font-medium"
          >
            Export CSV
          </button>
        </div>

        {/* Contributions Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading...</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-sritaru-50">
                  <tr>
                    {[
                      { key: "first_name", label: "First" },
                      { key: "last_name", label: "Last" },
                      { key: "whatsapp", label: "WhatsApp" },
                      { key: "email", label: "Email" },
                      { key: "num_trees", label: "Trees" },
                      { key: "dedicated_to", label: "Dedicated To" },
                      { key: "team_name", label: "Team" },
                      { key: "payment_status", label: "Status" },
                      { key: "notes", label: "Notes" },
                      { key: "created_at", label: "Date" },
                      ...(showArchived ? [{ key: "archived_reason", label: "Removed because" }] : []),
                    ].map((col) => (
                      <th
                        key={col.key}
                        onClick={() => handleSort(col.key)}
                        className="text-left py-3 px-3 text-gray-600 cursor-pointer hover:text-sritaru-700 whitespace-nowrap select-none"
                      >
                        {col.label}
                        <SortIcon col={col.key} />
                      </th>
                    ))}
                    <th className="py-3 px-3 text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contributions.map((c) => (
                    <tr
                      key={c.contribution_id}
                      className="border-b border-gray-100 hover:bg-sritaru-50"
                    >
                      <td className="py-2 px-3">{c.first_name}</td>
                      <td className="py-2 px-3">{c.last_name}</td>
                      <td className="py-2 px-3 whitespace-nowrap">
                        {c.whatsapp}
                      </td>
                      <td className="py-2 px-3">{c.email}</td>
                      <td className="py-2 px-3 font-bold text-sritaru-700">
                        {c.num_trees}
                      </td>
                      <td className="py-2 px-3">{c.dedicated_to}</td>
                      <td className="py-2 px-3">{c.team_name}</td>

                      {/* Editable payment_status */}
                      <td className="py-2 px-3">
                        {editingId === c.contribution_id && editField === "payment_status" ? (
                          <div className="flex items-center gap-1">
                            <select
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="border rounded px-1 py-1 text-xs"
                            >
                              {Object.entries(PAID_STATUS_LABELS).map(
                                ([k, v]) => (
                                  <option key={k} value={k}>
                                    {v}
                                  </option>
                                )
                              )}
                            </select>
                            <button
                              onClick={saveEdit}
                              className="text-green-600 hover:text-green-800 text-xs font-bold"
                            >
                              &#10003;
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="text-red-500 hover:text-red-700 text-xs font-bold"
                            >
                              &#10005;
                            </button>
                          </div>
                        ) : (
                          <span
                            onClick={() =>
                              startEdit(c.contribution_id, "payment_status", c.payment_status)
                            }
                            className={`cursor-pointer px-2 py-1 rounded-full text-xs font-medium ${
                              c.payment_status === "pledged"
                                ? "bg-yellow-100 text-yellow-700"
                                : c.payment_status === "zelle_paid" || c.payment_status === "planted"
                                ? "bg-green-100 text-green-700"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {PAID_STATUS_LABELS[c.payment_status] ||
                              c.payment_status}
                          </span>
                        )}
                      </td>

                      {/* Editable notes */}
                      <td className="py-2 px-3">
                        {editingId === c.contribution_id && editField === "notes" ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="border rounded px-1 py-1 text-xs w-24"
                              onKeyDown={(e) => {
                                if (e.key === "Enter") saveEdit();
                                if (e.key === "Escape") cancelEdit();
                              }}
                              autoFocus
                            />
                            <button
                              onClick={saveEdit}
                              className="text-green-600 hover:text-green-800 text-xs font-bold"
                            >
                              &#10003;
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="text-red-500 hover:text-red-700 text-xs font-bold"
                            >
                              &#10005;
                            </button>
                          </div>
                        ) : (
                          <span
                            onClick={() =>
                              startEdit(c.contribution_id, "notes", c.notes)
                            }
                            className="cursor-pointer text-gray-500 hover:text-gray-700 text-xs"
                          >
                            {c.notes || "(click to add)"}
                          </span>
                        )}
                      </td>

                      <td className="py-2 px-3 whitespace-nowrap">
                        {formatDate(c.created_at)}
                      </td>

                      {showArchived && (
                        <td className="py-2 px-3 text-xs text-gray-500">{c.archived_reason}</td>
                      )}

                      {/* Remove (archive) / restore */}
                      <td className="py-2 px-3">
                        {showArchived ? (
                          <button
                            onClick={() => handleRestore(c.contribution_id)}
                            className="text-sritaru-600 hover:text-sritaru-800 text-xs"
                          >
                            Restore
                          </button>
                        ) : deleteId === c.contribution_id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleArchive(c.contribution_id)}
                              className="bg-red-500 text-white text-xs px-2 py-1 rounded hover:bg-red-600"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setDeleteId(null)}
                              className="text-gray-500 text-xs px-2 py-1 hover:text-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteId(c.contribution_id)}
                            className="text-red-400 hover:text-red-600 text-xs"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {!loading && contributions.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No contributions found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Admin;
